import type { QuizSingleStep, QuizSteps } from "@/types/api.types";
import { getManualIcon } from "@/utils/iconHelpers";
import PrimaryButton from "@/components/atoms/PrimaryButton";
import SelectIcon from "@/components/atoms/SelectIcon";
import SelectBodyType from "@/components/atoms/SelectBodyType";
import QuizTitle from "@/components/atoms/QuizTitle";
import StickyAnswerCta from "@/components/atoms/StickyAnswerCta";
import { useStickyLastOption } from "@/hooks/useStickyLastOption";
import { useQuizData } from "@/hooks/useQuizData";

type QuizOption = NonNullable<QuizSingleStep["options"]>[number];

const NO_OPTIONS: QuizOption[] = [];

const SingleStepSelect = ({ step, answers, onAnswer }: QuizSteps) => {
  const isGenderStep = step.id === "gender";
  const isBodyTypeStep = step.id === "body_type";
  const gender = (answers?.["gender"] as string) || "male";
  const { data } = useQuizData();
  const stickySettings = data?.settings?.stickyLastAnswer;
  // Sticky last answer is opt-in per quiz and applies to the plain answer list only.
  const { listRef, stickyOption } = useStickyLastOption(
    stickySettings?.enabled && !isGenderStep && !isBodyTypeStep
      ? (step.options ?? NO_OPTIONS)
      : NO_OPTIONS,
  );

  return (
    <div className="mx-auto w-full">
      <QuizTitle
        title={step.title}
        description={step.subtitle}
        className="pb-9"
      />

      {isGenderStep ? (
        <div className="flex justify-center gap-4">
          {step.options?.map((option) => {
            const iconSrc =
              option.icon || getManualIcon(step.id, option.value, gender);
            return (
              <SelectIcon
                key={option.id}
                label={option.label}
                iconSrc={iconSrc}
                iconAlt={option.label}
                onClick={() => onAnswer(option.value)}
              />
            );
          })}
        </div>
      ) : isBodyTypeStep ? (
        <div className="flex justify-center gap-4 md:flex-row flex-col">
          {step.options?.map((option: QuizOption) => {
            const apiIcon =
              gender === "female" ? option.icon_female : option.icon_male;
            const iconSrc =
              apiIcon || getManualIcon(step.id, option.value, gender);
            return (
              <SelectBodyType
                key={option.id}
                label={option.label}
                iconSrc={iconSrc}
                iconAlt={option.label}
                onClick={() => onAnswer(option.value)}
              />
            );
          })}
        </div>
      ) : (
        <>
          <div ref={listRef} className="flex flex-col gap-4">
            {step.options?.map((option) => (
              <PrimaryButton
                key={option.id}
                label={option.label}
                onClick={() => onAnswer(option.value)}
              />
            ))}
          </div>

          {stickyOption && (
            <StickyAnswerCta
              label={stickyOption.label}
              onClick={() => onAnswer(stickyOption.value)}
              settings={stickySettings}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SingleStepSelect;
